// Copy of tylxm/design simple/theme.js at 9ef96a4.
import { useSyncExternalStore } from 'react';

/**
 * Theme is a document-level concern: `Popup` renders through `<dialog
 * showModal()>` into the browser's top layer, so a wrapper-scoped
 * `data-theme` would leave the dialog stuck in light mode. Everything here
 * writes to `<html>` instead.
 *
 * Two things drive the theme — the app bar's segmented control and the theme
 * select inside the account-settings screen — so the mode lives in one store
 * they both read from rather than in either component's state.
 */

const KEY = 'simple:theme';
const MODES = ['auto', 'light', 'dark'];

const mq = window.matchMedia('(prefers-color-scheme: dark)');
const listeners = new Set();

let mode = read();

function read() {
  try {
    const saved = localStorage.getItem(KEY);
    if (MODES.includes(saved)) return saved;
  } catch (e) {
    // Private windows and blocked site data both throw here; fall through.
  }
  return 'auto';
}

function apply() {
  const resolved = mode === 'auto' ? (mq.matches ? 'dark' : 'light') : mode;
  const root = document.documentElement;
  if (resolved === 'dark') root.setAttribute('data-theme', 'dark');
  else root.removeAttribute('data-theme');
}

function emit() {
  listeners.forEach((fn) => fn());
}

mq.addEventListener('change', () => {
  if (mode !== 'auto') return;
  apply();
  emit();
});

export const themeModes = MODES;

export function setTheme(next) {
  if (!MODES.includes(next) || next === mode) return;
  mode = next;
  try {
    localStorage.setItem(KEY, next);
  } catch (e) {
    // Not being able to remember the choice is not a reason to ignore it.
  }
  apply();
  emit();
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useTheme() {
  return useSyncExternalStore(subscribe, () => mode, () => mode);
}

apply();
